import { useEffect, useRef, useState } from 'react';

import { usePageTitle } from '../hooks/shared';
import { createElectionSim } from './electionLab/engine';
import MathExplainer from './electionLab/MathExplainer';

const SYSTEMS = [
  { key: 'fptp', label: 'FPTP' },
  { key: 'irv', label: 'Ranked (IRV)' },
  { key: 'pr', label: 'Proportional' },
  { key: 'approval', label: 'Approval' },
];

const DISTRIBUTIONS = [
  { value: 'uniform', label: 'Uniform spread' },
  { value: 'one', label: 'One center (bell)' },
  { value: 'two', label: 'Two clusters' },
  { value: 'three', label: 'Three clusters' },
];

const PAGE_STYLES = `
  .lab-root {
    --bg: #0b0f17; --panel: #121826; --panel-2: #171f30; --line: #26304a;
    --text: #e7ecf5; --muted: #93a0bd; --accent: #6ea8fe; --accent-2: #7ef0c2; --danger: #ff7a8a;
    background: radial-gradient(1200px 700px at 70% -10%, #16203a 0%, var(--bg) 55%);
    color: var(--text); min-height: 100vh; padding: 22px 18px 40px;
    font: 14px/1.45 -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  }
  .lab-root h1 { font-size: 22px; margin: 0 0 4px; letter-spacing: .2px; }
  .lab-root .sub { color: var(--muted); font-size: 13px; margin: 0 0 16px; max-width: 940px; }
  .lab-root .sub b { color: var(--text); font-weight: 600; }

  .lab-root .toolbar {
    display: flex; flex-wrap: wrap; gap: 10px; align-items: center;
    background: var(--panel); border: 1px solid var(--line); border-radius: 12px; padding: 10px 12px; margin-bottom: 12px;
  }
  .lab-root .toolbar .group { display: flex; gap: 6px; align-items: center; }
  .lab-root .toolbar .sep { width: 1px; height: 26px; background: var(--line); margin: 0 2px; }

  .lab-root button {
    background: var(--panel-2); color: var(--text); border: 1px solid var(--line);
    padding: 8px 14px; border-radius: 9px; cursor: pointer; font-size: 13px; font-weight: 600;
    transition: border-color .15s, background .15s, transform .05s;
  }
  .lab-root button:hover { border-color: var(--accent); }
  .lab-root button:active { transform: translateY(1px); }
  .lab-root button.primary { background: linear-gradient(180deg, #3f6fe0, #2f56c0); border-color: #4b7bf0; }
  .lab-root button.danger:hover { border-color: var(--danger); }
  .lab-root button:disabled { opacity: .4; cursor: default; }

  .lab-root .seg { display: inline-flex; background: var(--panel-2); border: 1px solid var(--line); border-radius: 9px; overflow: hidden; }
  .lab-root .seg button { border: none; border-radius: 0; background: transparent; padding: 8px 12px; }
  .lab-root .seg button.on { background: linear-gradient(180deg, #3f6fe0, #2f56c0); }

  .lab-root label.field { display: flex; flex-direction: column; gap: 3px; font-size: 11px; color: var(--muted); min-width: 120px; }
  .lab-root label.field .val { color: var(--text); font-weight: 700; font-size: 12px; }
  .lab-root input[type=range] { width: 100%; accent-color: var(--accent); }
  .lab-root select { background: var(--panel-2); color: var(--text); border: 1px solid var(--line); border-radius: 8px; padding: 7px 8px; }
  .lab-root .chk { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--muted); cursor: pointer; }
  .lab-root .chk input { accent-color: var(--accent); }

  .lab-root .stage { display: grid; grid-template-columns: 1.15fr 1fr; gap: 14px; align-items: stretch; }
  @media (max-width: 1050px) { .lab-root .stage { grid-template-columns: 1fr; } }

  .lab-root .card { background: var(--panel); border: 1px solid var(--line); border-radius: 12px; padding: 12px; }
  .lab-root .card h2 { font-size: 12px; text-transform: uppercase; letter-spacing: .8px; color: var(--muted); margin: 0 0 8px; }
  .lab-root .card-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 8px; flex-wrap: wrap; }
  .lab-root .card-head h2 { margin: 0; }
  .lab-root .seg.small button { padding: 6px 10px; font-size: 12px; }
  .lab-root .caption { font-size: 12px; color: var(--muted); margin: 0 0 10px; }
  .lab-root .caption b { color: var(--text); font-weight: 600; }
  .lab-root canvas { display: block; width: 100%; border-radius: 8px; background: #0a0e18; }
  .lab-root canvas.spec { height: 540px; }
  .lab-root canvas.chart { height: 210px; }
  @media (max-width: 640px) { .lab-root canvas.spec { height: 360px; } }

  .lab-root .right { display: flex; flex-direction: column; gap: 14px; }
  .lab-root .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
  .lab-root .stat { background: var(--panel-2); border: 1px solid var(--line); border-radius: 10px; padding: 8px 10px; }
  .lab-root .stat .k { color: var(--muted); font-size: 10px; text-transform: uppercase; letter-spacing: .6px; }
  .lab-root .stat .v { font-size: 18px; font-weight: 700; margin-top: 2px; }

  .lab-root .note { font-size: 12px; color: var(--muted); margin-top: 8px; }
  .lab-root .badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 12px; font-weight: 700; }
  .lab-root .badge.two { background: #17352a; color: var(--accent-2); border: 1px solid #23624a; }
  .lab-root .badge.multi { background: #2a2340; color: #c8b3ff; border: 1px solid #4a3a78; }

  .lab-root .legend { display: flex; flex-wrap: wrap; gap: 8px 14px; margin-top: 10px; }
  .lab-root .legend .item { display: flex; align-items: center; gap: 6px; font-size: 12px; }
  .lab-root .legend .item .dot { width: 11px; height: 11px; border-radius: 3px; }
  .lab-root .legend .item.dead { opacity: .4; text-decoration: line-through; }

  .lab-root details.adv summary { cursor: pointer; color: var(--accent); font-size: 12px; font-weight: 600; }
  .lab-root .adv-grid { display: flex; flex-wrap: wrap; gap: 12px 16px; margin-top: 10px; }
`;

function ElectionLab() {
  usePageTitle('Election Lab');

  const spectrumRef = useRef(null);
  const enpRef = useRef(null);
  const shareRef = useRef(null);
  const simRef = useRef(null);

  const [system, setSystem] = useState('fptp');
  const [speed, setSpeed] = useState(10);
  const [magnitude, setMagnitude] = useState(5);
  const [voters, setVoters] = useState(1500);
  const [startParties, setStartParties] = useState(7);
  const [strategic, setStrategic] = useState(65);
  const [dist, setDist] = useState('one');
  const [mobility, setMobility] = useState(35);
  const [deathPct, setDeathPct] = useState(6);
  const [entrants, setEntrants] = useState(false);
  const [viewMode, setViewMode] = useState('ecosystem');

  const [running, setRunning] = useState(false);
  const [stats, setStats] = useState(null);
  const [showMath, setShowMath] = useState(false);

  // Create the imperative engine once and wire it to the three canvases.
  useEffect(() => {
    const sim = createElectionSim({
      spectrum: spectrumRef.current,
      enpChart: enpRef.current,
      shareChart: shareRef.current,
      onStats: setStats,
      onRunningChange: setRunning,
    });
    simRef.current = sim;

    const onResize = () => sim.redraw();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      sim.destroy();
      simRef.current = null;
    };
  }, []);

  // Push control changes into the engine. Live params take effect immediately;
  // voters / starting-parties / landscape are applied on the next Reset (they
  // require reseeding), matching the standalone sandbox's behaviour.
  useEffect(() => {
    const sim = simRef.current;
    if (!sim) return;
    sim.setConfig({
      system,
      framesPerCycle: Math.max(1, 31 - speed),
      magnitude,
      voters,
      startParties,
      strategic: strategic / 100,
      dist,
      mobility: mobility / 100,
      deathThreshold: deathPct / 100,
      entrants,
      viewMode,
    });
    sim.redraw();
  }, [
    system,
    speed,
    magnitude,
    voters,
    startParties,
    strategic,
    dist,
    mobility,
    deathPct,
    entrants,
    viewMode,
  ]);

  const VERDICT_LABELS = { two: 'Two-party', multi: 'Multi-party' };
  const verdictLabel = VERDICT_LABELS[stats?.verdict] ?? '—';

  return (
    <div className="lab-root">
      <style>{PAGE_STYLES}</style>

      <h1>Election Lab — will this system evolve toward two parties?</h1>
      <p className="sub">
        Watch parties <b>evolve, consolidate, and die</b> under different voting rules. Each cycle
        is an election: voters back the nearest party (and may vote <b>strategically</b>), seats are
        awarded by the chosen system, then parties drift to chase votes. The{' '}
        <b>Effective Number of Parties</b> chart is the payoff — under single-winner rules it tends
        toward <b>2</b> (Duverger’s Law); raise the district magnitude and it climbs toward{' '}
        <b>M + 1</b>.
      </p>

      <div className="toolbar" style={{ marginBottom: 14 }}>
        <button className={showMath ? 'primary' : ''} onClick={() => setShowMath((v) => !v)}>
          {showMath ? '← Back to the simulator' : '📐 Show the math & mini-demos'}
        </button>
      </div>

      {showMath && <MathExplainer />}

      <div style={{ display: showMath ? 'none' : 'block' }}>
        <div className="toolbar">
          <div className="group seg">
            {SYSTEMS.map((s) => (
              <button
                key={s.key}
                className={system === s.key ? 'on' : ''}
                onClick={() => setSystem(s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="sep" />
          <div className="group">
            <button className={running ? '' : 'primary'} onClick={() => simRef.current?.toggle()}>
              {running ? '⏸ Pause' : '▶ Play'}
            </button>
            <button onClick={() => simRef.current?.step()}>Step</button>
            <button className="danger" onClick={() => simRef.current?.reset()}>
              Reset
            </button>
          </div>
          <div className="sep" />
          <label className="field" style={{ minWidth: 150 }}>
            <span>
              Speed <span className="val">{speed}</span>
            </span>
            <input
              type="range"
              min="1"
              max="30"
              value={speed}
              onChange={(e) => setSpeed(+e.target.value)}
            />
          </label>
          <label className="field" style={{ minWidth: 150, opacity: system === 'pr' ? 1 : 0.35 }}>
            <span>
              District seats (M) <span className="val">{magnitude}</span>
            </span>
            <input
              type="range"
              min="1"
              max="8"
              value={magnitude}
              disabled={system !== 'pr'}
              onChange={(e) => setMagnitude(+e.target.value)}
            />
          </label>
        </div>

        <div className="toolbar">
          <label className="field">
            <span>
              Voters <span className="val">{voters}</span>
            </span>
            <input
              type="range"
              min="200"
              max="4000"
              step="100"
              value={voters}
              onChange={(e) => setVoters(+e.target.value)}
            />
          </label>
          <label className="field">
            <span>
              Starting parties <span className="val">{startParties}</span>
            </span>
            <input
              type="range"
              min="2"
              max="10"
              value={startParties}
              onChange={(e) => setStartParties(+e.target.value)}
            />
          </label>
          <label className="field">
            <span>
              Strategic voting <span className="val">{strategic}%</span>
            </span>
            <input
              type="range"
              min="0"
              max="100"
              value={strategic}
              onChange={(e) => setStrategic(+e.target.value)}
            />
          </label>
          <label className="field">
            <span>Opinion landscape</span>
            <select value={dist} onChange={(e) => setDist(e.target.value)}>
              {DISTRIBUTIONS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
          <details className="adv">
            <summary>Advanced</summary>
            <div className="adv-grid">
              <label className="field">
                <span>
                  Party mobility <span className="val">{mobility}%</span>
                </span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={mobility}
                  onChange={(e) => setMobility(+e.target.value)}
                />
              </label>
              <label className="field">
                <span>
                  Death threshold <span className="val">{deathPct}%</span>
                </span>
                <input
                  type="range"
                  min="0"
                  max="20"
                  value={deathPct}
                  onChange={(e) => setDeathPct(+e.target.value)}
                />
              </label>
              <label className="chk">
                <input
                  type="checkbox"
                  checked={entrants}
                  onChange={(e) => setEntrants(e.target.checked)}
                />{' '}
                Allow new parties to emerge
              </label>
            </div>
          </details>
        </div>

        <div className="stage">
          <div className="card">
            <div className="card-head">
              <h2>
                {viewMode === 'ecosystem'
                  ? 'Feeding ground — creatures eating voter-food'
                  : 'Opinion space — parties chasing votes'}
              </h2>
              <div className="seg small">
                <button
                  className={viewMode === 'ecosystem' ? 'on' : ''}
                  onClick={() => setViewMode('ecosystem')}
                >
                  🎮 Creatures
                </button>
                <button
                  className={viewMode === 'analyst' ? 'on' : ''}
                  onClick={() => setViewMode('analyst')}
                >
                  📊 Analyst
                </button>
              </div>
            </div>
            {viewMode === 'ecosystem' && (
              <p className="caption">
                Each little dot is a <b>voter’s vote</b> (food). Every creature is a <b>party</b>{' '}
                that eats the food nearest it and <b>grows</b>; its eyes point where it’s hunting
                for more. Starve below the threshold and it
                <b> dies</b>. A creature that wins a seat gets a <b>crown</b>. Watch how
                winner-take-all rules end with the map ruled by ~2 giant creatures — that’s the
                two-party tendency.
              </p>
            )}
            <canvas ref={spectrumRef} className="spec" />
            <div className="legend">
              {(stats?.parties ?? []).map((p) => (
                <div key={p.id} className={`item${p.alive ? '' : ' dead'}`}>
                  <span className="dot" style={{ background: p.color }} />P{p.id} ·{' '}
                  {(p.share * 100).toFixed(0)}%
                </div>
              ))}
            </div>
          </div>

          <div className="right">
            <div className="card">
              <div className="stats">
                <div className="stat">
                  <div className="k">Election cycle</div>
                  <div className="v">{stats?.cycle ?? 0}</div>
                </div>
                <div className="stat">
                  <div className="k">Living parties</div>
                  <div className="v">{stats?.living ?? 0}</div>
                </div>
                <div className="stat">
                  <div className="k">Eff. № parties</div>
                  <div className="v">{stats?.enp ? stats.enp.toFixed(2) : '—'}</div>
                </div>
                <div className="stat">
                  <div className="k">Verdict</div>
                  <div className="v">
                    {stats?.verdict ? (
                      <span className={`badge ${stats.verdict}`}>{verdictLabel}</span>
                    ) : (
                      '—'
                    )}
                  </div>
                </div>
              </div>
              <div className="note">{stats?.note}</div>
            </div>

            <div className="card">
              <h2>Effective number of parties over time</h2>
              <canvas ref={enpRef} className="chart" />
            </div>

            <div className="card">
              <h2>Vote share over time</h2>
              <canvas ref={shareRef} className="chart" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ElectionLab;
